/*global*/
'use strict';

const BaseModel       = require('./Base');
const CompanyLocation = require('./CompanyLocation');
const moment          = require('moment');
let Model;

module.exports = Model = BaseModel.extend('Company',
/** @lends module:models.Company */
{
    /**
     * @default
     * @type {object}
     * @protected
     */
    defaults: {
        user: null,

        companyName:    '',
        type:           '',
        eNumber:        '',
        regNumber:      '',
        taxRegNumber:   '',
        epfRegNumber:   '',
        socsoRegNumber: '',
        zakatRefNumber: '',

        contactEmail: '',
        contactName:  '',

        employeeCount: 0,

        isSetUp: false,

        bankAccount: null,

        emailPayslipsText:             '',
        employeePayslipEmailText:      '',
        isEnabledEmailPayslips:        false,
        isEnabledEmployeePayslipEmail: false,
        isEnabledLeaveEmail:           false,

        recordUnpaidLeaveInPayroll:     true,
        unpaidLeaveCalculationType:     1,
        unpaidLeaveFixedDays:           22,
        areNationalHolidaysWorking:     false,
        workingHoursPerDay:             8,
        showBirthdaysInCalendar:        true,
        showAnniversariesInCalendar:    true,
        showCompanyHolidaysInCalendar:  true,
        showNationalHolidaysInCalendar: true,
        showLeavePolicies:              false,
        showAdditionalLeaveInfo:        '',
        nonWorkingDays:                 [1,7],
        holidayStates:                  ['national'],
        leaveRenewalPolicy:             'joinDate',
        annualLeavePolicy:              1,
        allowLeaveCarryForward:         true,
        daysCanCarryForward:            5,
        carryForwardExpiryMonths:       3,
        leaveDays:                      {},
        eisEnabled:                     true,
        payslipSize:                    'A4',
        hrdfContribution:               'auto'
    },

    /**
     * Company registration numbers against government authorities
     * plus labels.
     *
     * @type {object}
     * @default
     * @protected
     */
    companyNumbers: {
        eNumber:        'E Number',
        regNumber:      'Company Registration Number',
        epfRegNumber:   'EPF Number',
        socsoRegNumber: 'SOCSO Number'
    },

    /**
     * Validation constraints
     *
     * @type {object}
     * @protected
     */
    constraints: {
        user:         {presence: true},

        companyName:  {presence: {message: '^Company Name cannot be blank'}},
        type:         {presence: true},
        eNumber:      {
            format: {
                pattern: /^\d{1,8}-\d{2}$/,
                message: 'Invalid E Number format (Example: 99999999-99)'
            },
            length: {
                is:      11,
                message: '^The E Number must be 10 digits long and a hyphen'
            }
        },
        socsoRegNumber: {
            format: {
                pattern: /^[a-zA-Z]([0-9]{10})[a-zA-Z]$/,
                message: 'Invalid SOCSO Number format (Example: B3201002997A)'
            }
        },
        contactEmail:  {presence: true, email: true},
        contactName:   {presence: true},
        employeeCount: {
            numericality: {
                onlyInteger:          true,
                greaterThanOrEqualTo: 0
            }
        },
        annualLeaveDays: {numericality: true},
        hospitalizationLeaveDays: {numericality: true},
        maternityLeaveDays: {numericality: true},
        medicalLeaveDays: {numericality: true},
        marriageLeaveDays: {numericality: true},
        compassionateLeaveDays: {numericality: true},
        reminderApproveLeaveEmailDays: {
            numericality: {
                onlyInteger:          true,
                greaterThanOrEqualTo: 0
            }
        }
    },

    /**
     * Grouped validation constraints
     *
     * @default
     * @type {object}
     * @protected
     */
    groupedConstraints: {
        leaveSettings: {
            unpaidLeaveCalculationType: function(value, attrs) {
                if (attrs.recordUnpaidLeaveInPayroll === true) {
                    return {
                        numericality: {
                            onlyInteger:          true,
                            greaterThanOrEqualTo: 1,
                            lessThanOrEqualTo:    3
                        },
                        presence: true
                    };
                }

                return null;
            },

            unpaidLeaveFixedDays: function(value, attrs) {
                var r = {
                    numericality: {
                        onlyInteger:          true,
                        greaterThanOrEqualTo: 1,
                        lessThanOrEqualTo:    31
                    }
                };

                if (
                    attrs.recordUnpaidLeaveInPayroll === true &&
                    attrs.unpaidLeaveCalculationType === 1
                ) {
                    r = _.extend(r, {presence: true});
                }

                return r;
            }
        }
    },

    /**
     * @constructs
     * @augments module:models.BaseModel
     *
     * @prop {external:Parse.User} user - The user associated to the company
     * @prop {string} companyName
     * @prop {string} type
     * @prop {string} eNumber
     * @prop {string} regNumber
     * @prop {string} taxRegNumber
     * @prop {string} epfRegNumber
     * @prop {string} socsoRegNumber
     * @prop {string} zakatRefNumber
     * @prop {string} contactEmail
     * @prop {string} contactName
     * @prop {integer} employeeCount
     * @prop {boolean} isSetUp
     * @prop {module:models.BankAccount} bankAccount
     * @prop {string} emailPayslipText
     * @prop {string} employeePayslipEmailText
     * @prop {boolean} isEnabledEmailPayslips
     * @prop {boolean} isEnabledEmployeePayslipEmail
     * @prop {boolean} isEnabledLeaveEmail
     * @prop {boolean} reminderApproveLeaveEmailDays
     * @prop {boolean} recordUnpaidLeaveInPayroll
     * @prop {integer} unpaidLeaveCalculationType
     * @prop {integer} unpaidLeaveFixedDays
     * @prop {integer} leaveDayRoundingType
     * @prop {float} leaveDayRoundingSize
     * @prop {integer} leaveBalanceResetType
     * @prop {integer} leaveBalanceResetMonth
     * @prop {integer} leaveBalanceResetDay
     * @prop {boolean} areNationalHolidaysWorking
     * @prop {integer} workingHoursPerDay
     * @prop {boolean} showBirthdaysInCalendar
     * @prop {boolean} showAnniversariesInCalendar
     * @prop {boolean} showCompanyHolidaysInCalendar
     * @prop {boolean} showNationalHolidaysInCalendar
     * @prop {boolean} showLeavePolicies
     * @prop {string} showAdditionalLeaveInfo
     * @prop {array<integer>} nonWorkingDays
     * @prop {array<string>} holidayStates
     * @prop {boolean} eisEnabled - If EIS should be calculated for this
     *  employer or not
     */
    initialize: function() {
        BaseModel.prototype.initialize.apply(this, arguments);

        this.on(
            'change:recordUnpaidLeaveInPayroll',
            this.toggleUnpaidLeaveFixedDays,
            this
        );
        this.on(
            'change:unpaidLeaveCalculationType',
            this.toggleUnpaidLeaveFixedDays,
            this
        );

        this.primaryLocation = null;
    },

    /**
     * Check if the company is fully setup
     *
     * @instance
     * @returns {boolean} true if it is
     */
    isSetUp: function() {
        return (
            this.get('isSetUp') || (
                this.isBasicInfoSetUp() &&
                this.get('regNumber') &&
                this.get('eNumber') &&
                this.get('epfRegNumber') &&
                this.get('socsoRegNumber')
            )
        );
    },

    /**
     * Check if the company has a location, a name, a type and an address
     *
     * @instance
     * @returns {boolean} true if it does
     */
    isBasicInfoSetUp: function() {
        return (
            this.get('isSetUp') || (
                this.primaryLocation &&
                this.get('companyName') &&
                this.get('type')
            )
        );
    },

    /**
     * Get the primary location of this company
     *
     * @instance
     * @param {boolean} options.forceFetch - If true we fetch from the server
     *  no matter what. If false we can use a previously fetched reference if
     *  available
     * @returns {module:models.CompanyLocation} The primary location
     */
    getPrimaryLocation: function(options) {
        options = _.defaults(options || {}, {
            forceFetch: false
        });

        if (this.primaryLocation && options.forceFetch !== true) {
            return Parse.Promise.as(this.primaryLocation);
        }

        return CompanyLocation.fetchPrimaryForCompany(this).then(location => {
            this.primaryLocation = location;
            return Parse.Promise.as(location);
        });
    },

    /**
     * Set unpaidLeaveFixedDays to null if the leave settings don't require
     * a value in unpaidLeaveFixedDays
     *
     * @instance
     */
    toggleUnpaidLeaveFixedDays: function() {
        if (
            this.get('recordUnpaidLeaveInPayroll') !== true ||
            this.get('unpaidLeaveCalculationType') !== 1
        ) {
            this.set({'unpaidLeaveFixedDays': null});
        }
    },

    /**
     * Like {@link module:models.BaseModel#fetch} but we make sure to fetch
     * the primary location as well, see
     * {@link module:models.Company#getPrimaryLocation}
     *
     * Also bank account is included
     *
     * @instance
     * @returns {module:models.Company} the company
     */
    fetch: function() {
        return this.getPrimaryLocation({forceFetch: true})
            .then(
                () => BaseModel.prototype.fetch.call(this, {success: () => {}})
            )
            .then(model => Parse.Promise.as().then(() => {
                if (!model.get('bankAccount')) {
                    return true;
                }

                return model.get('bankAccount').fetch();
            }).then(() => {
                model.trigger('sync', model);
                return model;
            }));
    },

    /**
     * Generate the company's form E
     *
     * @instance
     * @returns {Parse.Promise<?>} A promise... of what?
     */
    generateFormE: function() {
        return Parse.Cloud.run('generateFormE', {companyId: this.id});
    },

    generateFormCp8d: function() {
        return Parse.Cloud.run('generateFormCp8d', {companyId: this.id});
    },

    getLeaveDays: function(type) {
        let days = this.get('leaveDays') || {};
        return parseFloat(days[type.id]) || 0;
    },

    setLeaveDays: function(options) {
        let {type, typeId, days} = options;
        let leaveDays = this.get('leaveDays') || {};
        if (!typeId) {
            typeId = type.id;
        }
        leaveDays[typeId] = parseFloat(days || 0);
        this.set('leaveDays', leaveDays);
    },

    isWorkingDay: function(date) {
        if (!(date instanceof moment)) {
            date = moment(date);
        }

        if (!date.isValid()) {
            return false;
        }

        if (_(this.get('nonWorkingDays')).contains(date.day() + 1)) {
            return false;
        }

        let isHoliday = app.holidays.find(holiday => {
            if (moment(holiday.get('date')).isSame(date, 'day')) {
                return _.intersection(
                    holiday.get('states'),
                    this.get('holidayStates')
                ).length > 0;
            }
        });

        return !isHoliday;
    },

    getWorkingDaysInMonth: function(date) {
        date = date || moment();
        const start = date.clone().startOf('month');
        const end   = date.clone().endOf('month');
        let workingDays = [];

        while (!start.isAfter(end)) {
            if (this.isWorkingDay(start)) {
                workingDays.push(start.clone());
            }
            start.add(1, 'day');
        }

        return workingDays;
    }
}, {
    fetchById: function(id) {
        return (new Parse.Query('Company'))
            .include('user')
            .include('bankAccount')
            .include('mppAdmin')
            .get(id)
            .then(company => company.getPrimaryLocation().then(
                () => Parse.Promise.as(company)
            ));
    },

    fetchForUser: function(user) {
        if (user.get('role') === 'employee') {
            return Model.fetchForEmployee(user);
        }

        return (new Parse.Query('Company'))
            .include('user')
            .include('bankAccount')
            .include('mppAdmin')
            .equalTo('user', user)
            .first()
            .then(function(company) {
                if (!company) {
                    return Parse.Promise.error('Couldn\'t find company.');
                }

                return company.getPrimaryLocation().then(function() {
                    return Parse.Promise.as(company);
                });
            });
    },

    fetchForEmployee: function(user) {
        return (new Parse.Query('Employee'))
            .include('user')
            .include('company')
            .include('company.bankAccount')
            .include('company.mppAdmin')
            .equalTo('user', user)
            .first()
            .then(function(employee) {
                return employee.get('company');
            });
    }
});



// WEBPACK FOOTER //
// ./app/models/Company.js